<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :append-to-body="true"
    custom-class="superdoc-popup-dialog"
    :show-close="false"
    :close-on-click-modal="false"
    @open="handleOpen"
    @close="handleClose"
  >
    <div class="superdoc-popup">
      <div class="superdoc-popup-header">
        <div class="flex items-center gap-2">
          <img class="superdoc-popup-logo" src="@public/assets/images/ic_superdoc_ava.svg" alt="SuperDoc" />
          <span class="superdoc-popup-title">SuperDoc</span>
        </div>
        <button type="button" class="superdoc-popup-close" @click="dialogVisible = false">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <div class="superdoc-popup-content">
        <aside class="superdoc-popup-sidebar" :class="{ 'superdoc-popup-sidebar--expanded': isHistoryPanelExpanded }">
          <div class="history-panel-head" :class="{ 'history-panel-head--expanded': isHistoryPanelExpanded }">
            <span v-if="isHistoryPanelExpanded" class="history-panel-title">Lịch sử</span>
            <button type="button" class="history-panel-toggle" @click="toggleHistoryPanel()">
              <i :class="isHistoryPanelExpanded ? 'el-icon-arrow-left' : 'el-icon-arrow-right'"></i>
            </button>
          </div>
          <button type="button" class="new-chat-btn" :class="{ 'new-chat-btn--collapsed': !isHistoryPanelExpanded }" @click="createNewConversation()">
            <i class="el-icon-plus" :class="{ 'mr-1': isHistoryPanelExpanded }"></i>
            <span v-if="isHistoryPanelExpanded">Hội Thoại mới</span>
          </button>
          <div class="history-list">
            <div
              v-for="conversation in conversations"
              :key="conversation.id"
              class="history-item"
              :class="{
                active: conversation.id === activeConversationId,
                'history-item--collapsed': !isHistoryPanelExpanded
              }"
            >
              <div v-if="isHistoryPanelExpanded" class="history-item-inner">
                <button
                  type="button"
                  class="history-main-btn"
                  :title="getConversationTitle(conversation)"
                  @click="switchConversation(conversation.id)"
                >
                  <p class="history-title">{{ getConversationTitle(conversation) }}</p>
                </button>
                <div class="history-actions">
                  <el-popover
                    placement="bottom-end"
                    width="156"
                    trigger="click"
                    popper-class="history-actions-popover"
                  >
                    <div class="history-action-menu">
                      <button type="button" @click="renameConversation(conversation.id)">
                        <i class="el-icon-edit"></i>
                        Đổi tên
                      </button>
                      <button type="button" class="history-action-menu__danger" @click="deleteConversation(conversation.id)">
                        <i class="el-icon-delete"></i>
                        Xóa
                      </button>
                    </div>
                    <button
                      slot="reference"
                      type="button"
                      class="history-icon-btn"
                      aria-label="Tùy chọn hội thoại"
                      @click.stop
                    >
                      <i class="el-icon-more"></i>
                    </button>
                  </el-popover>
                </div>
              </div>
              <el-tooltip v-else effect="dark" placement="right" :content="getHistoryTooltipContent(conversation)">
                <button
                  type="button"
                  class="history-avatar-btn"
                  :class="{ active: conversation.id === activeConversationId }"
                  @click="switchConversation(conversation.id)"
                >
                  {{ getHistoryIconText(conversation) }}
                </button>
              </el-tooltip>
            </div>
          </div>
        </aside>

        <section class="superdoc-popup-chat">
          <div
            ref="messageContainer"
            class="superdoc-popup-messages"
            :class="{ 'welcome-centered': showWelcomeSection }"
          >
            <SuperDocWelcome
              v-if="showWelcomeSection"
              class="message-item"
              :message="activeConversation.aiWelcomeMessage"
              :subtitle="$t('lbl_super_doc.lbl_input_placeholder')"
              :markdown-renderer="compiledMarkdown"
            />
            <template v-for="(item, index) in activeConversation.messages">
              <SuperDocQuestionMessage
                :key="`q-${activeConversation.id}-${index}`"
                :ref="index === activeConversation.messages.length - 1 ? 'latestQuestion' : null"
                :content="item.question"
                :image-attachments="item.images"
                :file-attachments="item.files"
                @copy="copyMessage"
              />
              <div :key="`e-${activeConversation.id}-${index}`" v-if="item.error">
                <div v-if="!activeConversation.reSending && index === activeConversation.messages.length - 1"
                  class="flex justify-content-end mb-2">
                  <div class="font-xs text-gray-600 text-xs flex items-center">
                    <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                      $t('lbl_super_doc.lbl_error_occurred_msg') }} <span class="underline cursor-pointer"
                        @click="reSendMessage(item.question, index)">{{ $t('lbl_super_doc.lbl_error_try_again_msg')
                        }}</span></span>
                  </div>
                </div>
                <div v-else class="flex justify-content-end mb-2">
                  <div class="font-xs text-gray-600 text-xs flex items-center">
                    <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                      $t('lbl_super_doc.lbl_error_occurred_msg') }}</span>
                  </div>
                </div>
              </div>
              <div
                :key="`t-${activeConversation.id}-${index}`"
                v-if="activeConversation.thinking && index === activeConversation.messages.length - 1"
                class="dot-loading-wrap"
              >
                <span class="dot-loading">
                  <span class="dot-loading-item"></span>
                  <span class="dot-loading-item"></span>
                  <span class="dot-loading-item"></span>
                </span>
              </div>
              <SuperDocAnswerMessage
                :key="`a-${activeConversation.id}-${index}`"
                :ref="index === activeConversation.messages.length - 1 ? 'latestAnswer' : null"
                v-if="item.answer !== ''"
                :content="item.answer"
                :markdown-renderer="compiledMarkdown"
                @copy="copyMessage"
                @print="printAnswer(item.question, $event)"
                @pdf="exportAnswerPdf(item.question, $event)"
              />
            </template>
          </div>
          <div v-if="showScrollToBottom" class="chat-scroll-actions">
            <button type="button" class="chat-scroll-btn" title="Cuộn xuống cuối hội thoại" aria-label="Cuộn xuống cuối hội thoại" @click="scrollToBottom">
              <i class="el-icon-arrow-down"></i>
            </button>
          </div>

          <div class="superdoc-popup-input">
            <SuperDocInputArea
              ref="inputArea"
              :value="question"
              :thinking="activeConversation.thinking"
              :max-files="2"
              :max-images="3"
              @input="question = $event"
              @send="sendMessage"
            />
            <div><small class="font-xs text-gray-400 text-xs">{{ $t('lbl_super_doc.lbl_note_msg') }}</small></div>
          </div>
        </section>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import superdocService from '@/services/superdocService'
import { formatSuperDocHtml, stripHtml } from '@/utils/superdocFormatter'
import SuperDocAnswerMessage from './SuperDocAnswerMessage.vue'
import SuperDocInputArea from './SuperDocInputArea.vue'
import SuperDocQuestionMessage from './SuperDocQuestionMessage.vue'
import SuperDocWelcome from './SuperDocWelcome.vue'

export default {
  name: 'SuperDocChatPopup',
  components: { SuperDocWelcome, SuperDocQuestionMessage, SuperDocAnswerMessage, SuperDocInputArea },
  data () {
    return {
      dialogVisible: false,
      conversations: [],
      activeConversationId: null,
      question: '',
      askAbortController: null,
      isHistoryPanelExpanded: false,
      showScrollToBottom: false,
      scrollThreshold: 160
    }
  },
  computed: {
    activeConversation () {
      return this.conversations.find(item => item.id === this.activeConversationId) || {
        id: null,
        sessionId: null,
        agentType: 'type_2.2',
        title: 'Hội Thoại mới',
        aiWelcomeMessage: this.$t('lbl_super_doc.lbl_welcome_msg'),
        messages: [],
        history: [],
        hasLoadedMessages: false,
        thinking: false,
        reSending: false
      }
    },
    showWelcomeSection () {
      return this.activeConversation.aiWelcomeMessage && this.activeConversation.messages.length === 0
    }
  },
  beforeDestroy () {
    this.unbindMessageScroll()
    if (this.askAbortController) {
      this.askAbortController.abort()
      this.askAbortController = null
    }
    this.conversations.forEach((conversation) => {
      ;(conversation.messages || []).forEach((message) => this.releaseMessageUploads(message))
    })
  },
  methods: {
    handleOpen () {
      this.isHistoryPanelExpanded = true
      this.$nextTick(() => this.bindMessageScroll())
    },
    async openAndActivateFirstConversation () {
      this.dialogVisible = true
      await this.ensureHistoryLoaded()
      this.$nextTick(() => {
        if (!this.conversations.length) {
          this.createNewConversation()
          return
        }

        this.activeConversationId = this.conversations[0].id
        this.question = ''
        const container = this.$refs.messageContainer
        if (container) container.scrollTop = 0
        if (this.conversations[0].messages?.length) {
          this.scrollToLatestQuestion()
        } else {
          this.handleMessageScroll()
        }
        this.focusInputArea()
      })
    },
    async openWithQuestion (query) {
      this.dialogVisible = true
      await this.ensureHistoryLoaded()
      this.$nextTick(() => {
        this.createNewConversation(query)
      })
    },
    createConversationObject (payload = {}) {
      const now = new Date().toISOString()
      return {
        id: payload.id || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        sessionId: payload.sessionId || null,
        agentType: payload.agentType || 'type_2.2',
        title: payload.title || 'Hội Thoại mới',
        isManualTitle: Boolean(payload.isManualTitle),
        aiWelcomeMessage: this.$t('lbl_super_doc.lbl_welcome_msg'),
        messages: payload.messages || [],
        history: payload.history || [],
        createdAt: payload.createdAt || now,
        updatedAt: payload.updatedAt || now,
        hasLoadedMessages: Boolean(payload.hasLoadedMessages),
        thinking: false,
        reSending: false
      }
    },
    mapHistoryToConversation (session) {
      const sessionId = session?.id || session?.session_id
      return this.createConversationObject({
        id: `session-${sessionId}`,
        sessionId,
        agentType: session?.agent_type || session?.ask_type || 'type_2.2',
        title: session?.title || `Hội Thoại ${sessionId}`,
        isManualTitle: Boolean(session?.title),
        createdAt: session?.created_at,
        updatedAt: session?.updated_at,
        hasLoadedMessages: false
      })
    },
    mapMessagesToConversationHistory (messages = []) {
      return messages
        .filter(item => item?.question || item?.answer)
        .map(item => ({
          question: item.question || '',
          answer: item.answer || ''
        }))
    },
    normalizeAttachmentList (items = [], defaultPrefix = 'file') {
      if (!Array.isArray(items)) return []
      return items
        .map((item, idx) => {
          if (!item) return null
          if (typeof item === 'string') {
            return {
              url: item,
              name: `${defaultPrefix}-${idx + 1}`,
              size: null
            }
          }
          const url = item.url || item.file_url || item.path || ''
          if (!url) return null
          return {
            url,
            name: item.name || `${defaultPrefix}-${idx + 1}`,
            size: item.size || item.file_size || null
          }
        })
        .filter(Boolean)
    },
    extractMessageListFromResponse (messagesRes, detailData) {
      const fromMessagesApi =
        messagesRes?.data?.data ||
        messagesRes?.data?.messages ||
        messagesRes?.data ||
        []

      if (Array.isArray(fromMessagesApi) && fromMessagesApi.length) {
        return fromMessagesApi
      }

      const fromDetail = detailData?.history || []
      return Array.isArray(fromDetail) ? fromDetail : []
    },
    async ensureHistoryLoaded () {
      try {
        const historyRes = await superdocService.getHistory({ limit: 20, page: 1 })
        const sessions = historyRes?.data?.data || []
        const draftConversations = this.conversations.filter(item => !item.sessionId)
        this.conversations = [...draftConversations, ...sessions.map(item => this.mapHistoryToConversation(item))]

        if (!this.conversations.length) {
          this.createNewConversation()
          return
        }

        if (!this.activeConversationId || !this.conversations.find(item => item.id === this.activeConversationId)) {
          this.activeConversationId = this.conversations[0].id
        }

        const activeConversation = this.getActiveConversationRef()
        if (activeConversation) {
          await this.loadConversationDetail(activeConversation)
        }
      } catch (error) {
        if (!this.conversations.length) {
          this.createNewConversation()
        }
      }
    },
    async loadConversationDetail (conversation) {
      if (!conversation) return
      const sessionId = this.resolveConversationSessionId(conversation)
      if (!sessionId) return

      try {
        const shouldLoadDetail = !conversation.hasLoadedMessages
        const [detailRes, messagesRes] = await Promise.all([
          shouldLoadDetail ? superdocService.getSessionDetail(sessionId) : Promise.resolve(null),
          superdocService.getSessionMessages(sessionId)
        ])
        const detailData = detailRes?.data || null
        const messageData = this.extractMessageListFromResponse(messagesRes, detailData)

        const normalizedMessages = messageData.map(item => ({
          messageId: item.message_id || item.id || null,
          question: item.question || '',
          answer: item.answer || '',
          images: this.normalizeAttachmentList(item.images || item.image_urls || [], 'image'),
          files: this.normalizeAttachmentList(item.files || item.file_urls || [], 'file'),
          createdAt: item.created_at || item.createdAt || null
        }))

        if (detailData) {
          conversation.agentType = detailData.agent_type || detailData.ask_type || conversation.agentType || 'type_2.2'
          conversation.title = detailData.title || conversation.title || `Hội Thoại ${conversation.sessionId}`
          conversation.isManualTitle = Boolean(detailData.title)
          conversation.createdAt = detailData.created_at || conversation.createdAt
          conversation.updatedAt = detailData.updated_at || conversation.updatedAt
        }
        conversation.messages = normalizedMessages
        conversation.history = normalizedMessages.length
          ? this.mapMessagesToConversationHistory(normalizedMessages)
          : (detailData?.history || conversation.history || [])
        conversation.hasLoadedMessages = true
      } catch (error) {
        if (!conversation.hasLoadedMessages) {
          conversation.hasLoadedMessages = true
        }
      }
    },
    createNewConversation (initialQuestion = '') {
      const conversation = this.createConversationObject()
      this.conversations.unshift(conversation)
      this.activeConversationId = conversation.id
      this.question = ''
      this.$nextTick(() => {
        const container = this.$refs.messageContainer
        if (container) container.scrollTop = 0
        this.focusInputArea()
      })

      if (initialQuestion && initialQuestion.trim()) {
        this.question = initialQuestion.trim()
        this.sendMessage()
      }
    },
    async switchConversation (id) {
      this.activeConversationId = id
      this.question = ''
      const conversation = this.getActiveConversationRef()
      if (conversation) {
        await this.loadConversationDetail(conversation)
      }
      if (conversation?.messages?.length) {
        this.scrollToLatestQuestion()
      } else {
        this.$nextTick(() => {
          const container = this.$refs.messageContainer
          if (container) container.scrollTop = 0
          this.handleMessageScroll()
        })
      }
    },
    toggleHistoryPanel () {
      this.isHistoryPanelExpanded = !this.isHistoryPanelExpanded
    },
    focusInputArea () {
      this.$refs.inputArea && this.$refs.inputArea.focusInput()
    },
    getActiveConversationRef () {
      return this.conversations.find(item => item.id === this.activeConversationId)
    },
    resolveConversationSessionId (conversation) {
      if (!conversation) return null
      if (conversation.sessionId) return conversation.sessionId

      const conversationId = String(conversation.id || '')
      // Support history ids from API (raw number/string) and internal ids (`session-<id>`).
      if (!conversationId.startsWith('session-')) {
        if (!conversationId || conversationId.includes('_')) return null
        conversation.sessionId = conversationId
        return conversationId
      }

      const sessionId = conversationId.replace('session-', '').trim()
      if (!sessionId) return null
      conversation.sessionId = sessionId
      return sessionId
    },
    updateMessageAnswerByStreamEvent (message, streamEvent) {
      if (!message || !streamEvent) return

      if (streamEvent.mode === 'replace') {
        message.answer = streamEvent.text || ''
      } else if (streamEvent.text) {
        message.answer = `${message.answer || ''}${streamEvent.text}`
      }
    },
    resolveSessionIdFromStreamEvent (conversation, streamEvent) {
      const parsed = streamEvent?.parsed
      const sessionId = parsed?.session_id || parsed?.sessionId || parsed?.data?.session_id || parsed?.meta?.session_id
      if (sessionId && !conversation.sessionId) {
        conversation.sessionId = String(sessionId)
        if (!String(conversation.id || '').startsWith('session-')) {
          conversation.id = `session-${sessionId}`
          this.activeConversationId = conversation.id
        }
      }
    },
    async runAskStream ({ conversation, askQuestion, messageIndex, uploads = {}, isResend = false }) {
      if (!conversation) return

      conversation.thinking = true
      conversation.reSending = isResend
      conversation.updatedAt = new Date().toISOString()

      try {
        if (this.askAbortController) {
          this.askAbortController.abort()
        }
        this.askAbortController = new AbortController()

        await superdocService.askStream({
          lang: this.$lang || 'vi',
          question: askQuestion,
          agentType: 'type_2.2',
          sessionId: conversation.sessionId,
          files: [...(uploads.files || []), ...(uploads.images || [])],
          signal: this.askAbortController.signal,
          onChunk: (streamEvent) => {
            const message = conversation.messages[messageIndex]
            if (!message) return
            delete message.error
            this.resolveSessionIdFromStreamEvent(conversation, streamEvent)
            this.updateMessageAnswerByStreamEvent(message, streamEvent)
          }
        })

        const message = conversation.messages[messageIndex]
        if (message) {
          delete message.error
          conversation.history.push({
            question: askQuestion,
            answer: message.answer || ''
          })
        }
      } catch (error) {
        if (error?.name === 'AbortError') return
        const message = conversation.messages[messageIndex]
        if (message) message.error = true
        this.$message.error('An error occurred. Please try again later.')
      } finally {
        this.askAbortController = null
        conversation.thinking = false
        conversation.reSending = false
        conversation.updatedAt = new Date().toISOString()
      }
    },
    normalizeUploadPayload (payload = {}) {
      const images = (payload.images || []).filter(Boolean).map((file) => ({
        file,
        name: file.name || 'image',
        url: URL.createObjectURL(file),
        size: file.size || 0
      }))
      const files = (payload.files || []).filter(Boolean).map((file) => ({
        file,
        name: file.name || 'file',
        url: URL.createObjectURL(file),
        size: file.size || 0
      }))
      return { images, files }
    },
    releaseMessageUploads (message) {
      ;[...(message?.images || []), ...(message?.files || [])].forEach((item) => {
        if (item?.url && item.file) URL.revokeObjectURL(item.url)
      })
    },
    async sendMessage (payload = {}) {
      const conversation = this.getActiveConversationRef()
      if (!conversation) return
      if (conversation.thinking) return

      const askQuestion = (this.question || '').trim()
      const hasUploads = Boolean(payload.files?.length || payload.images?.length)
      if (!askQuestion && !hasUploads) return

      const normalizedUploads = this.normalizeUploadPayload(payload)
      this.question = ''
      conversation.messages.push({
        question: askQuestion,
        answer: '',
        images: normalizedUploads.images.map(item => ({ name: item.name, url: item.url, file: item.file, size: item.size })),
        files: normalizedUploads.files.map(item => ({ name: item.name, url: item.url, file: item.file, size: item.size }))
      })
      if (!conversation.isManualTitle) {
        conversation.title = conversation.messages[0]?.question?.replace(/<[^>]*>/g, '').slice(0, 40) || 'Hội Thoại mới'
      }
      this.scrollToLatestQuestion()
      await this.runAskStream({
        conversation,
        askQuestion,
        messageIndex: conversation.messages.length - 1,
        uploads: payload
      })
    },
    async reSendMessage (question, index) {
      const conversation = this.getActiveConversationRef()
      if (!conversation || !question) return
      if (!conversation.messages[index]) return
      this.releaseMessageUploads(conversation.messages[index])
      conversation.messages[index].answer = ''
      await this.runAskStream({
        conversation,
        askQuestion: question,
        messageIndex: index,
        isResend: true
      })
    },
    getConversationTitle (conversation) {
      const title = stripHtml(conversation?.title || '').replace(/\s+/g, ' ').trim()
      return title || 'Hội Thoại mới'
    },
    getHistoryTooltipContent (conversation) {
      if (!conversation) return ''
      const title = this.getConversationTitle(conversation)
      const maxChars = 10
      return title.length > maxChars ? `${title.slice(0, maxChars)}...` : title
    },
    getHistoryIconText (conversation) {
      const firstChar = this.getConversationTitle(conversation).charAt(0)
      return (firstChar || 'H').toUpperCase()
    },
    formatHistoryTime (value) {
      if (!value) return ''
      const date = new Date(value)
      return isNaN(date.getTime()) ? '' : date.toLocaleString()
    },
    async renameConversation (conversationId) {
      const conversation = this.conversations.find(item => item.id === conversationId)
      if (!conversation) return

      try {
        const { value } = await this.$prompt('Nhập tên hội thoại mới', 'Đổi tên hội thoại', {
          confirmButtonText: 'Lưu',
          cancelButtonText: 'Hủy',
          inputValue: this.getConversationTitle(conversation)
        })

        if (!value || !value.trim()) return
        conversation.title = value.trim().slice(0, 80)
        conversation.isManualTitle = true
        conversation.updatedAt = new Date().toISOString()
      } catch (error) {}
    },
    async deleteConversation (conversationId) {
      if (this.conversations.length <= 1) {
        this.$message.warning('Cần ít nhất 1 hội thoại')
        return
      }

      try {
        await this.$confirm('Bạn có chắc chắn muốn xóa hội thoại này?', 'Xóa hội thoại', {
          confirmButtonText: 'Xóa',
          cancelButtonText: 'Hủy',
          type: 'warning'
        })

        const idx = this.conversations.findIndex(item => item.id === conversationId)
        if (idx === -1) return
        this.conversations.splice(idx, 1)

        if (this.activeConversationId === conversationId) {
          this.activeConversationId = this.conversations[0]?.id || null
          this.question = ''
        }
      } catch (error) {}
    },
    compiledMarkdown (message) {
      return formatSuperDocHtml(message)
    },
    stripHtml (html) {
      return stripHtml(html)
    },
    async copyMessage (content) {
      const plainText = this.stripHtml(content).trim()
      if (!plainText) return

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(plainText)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = plainText
          textArea.setAttribute('readonly', '')
          textArea.style.position = 'absolute'
          textArea.style.left = '-9999px'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          textArea.remove()
        }
        this.$message.success('Đã sao chép vào bộ nhớ tạm')
      } catch (error) {
        this.$message.error('Không thể sao chép vào bộ nhớ tạm')
      }
    },
    escapeHtml (text = '') {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    createPrintWindow (title, questionText, answerHtml) {
      const printWindow = window.open('', '_blank', 'width=900,height=700')
      if (!printWindow) {
        this.$message.error('Khong mo duoc cua so in')
        return null
      }
      const safeQuestion = this.escapeHtml(this.stripHtml(questionText || '').trim())
      const questionSection = safeQuestion
        ? `
          <section class="qa-block">
            <h2>Cau hoi</h2>
            <div class="qa-question">${safeQuestion}</div>
          </section>
        `
        : ''

      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              * { box-sizing: border-box; }
              body { font-family: Arial, sans-serif; padding: 24px; line-height: 1.6; color: #0f172a; font-size: 14px; }
              h1 { font-size: 18px; margin: 0 0 16px; }
              h2, h3, h4 { margin: 16px 0 8px; line-height: 1.4; color: #0f172a; }
              p { margin: 0 0 10px; }
              ul, ol { margin: 0 0 10px 20px; padding: 0; }
              pre { margin: 10px 0; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; white-space: pre-wrap; }
              code { font-family: Menlo, Consolas, monospace; font-size: 12px; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 10px 0; border: 1px solid #cbd5e1; }
              th, td { border: 1px solid #cbd5e1; padding: 8px 10px; text-align: left; vertical-align: top; word-break: break-word; }
              th { background: #f1f5f9; font-weight: 600; color: #0f172a; }
              tr:nth-child(even) td { background: #f8fafc; }
              img { max-width: 100%; height: auto; }
              .qa-block { margin-bottom: 14px; }
              .qa-question { white-space: pre-wrap; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
              @media print { body { padding: 12px; } }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            ${questionSection}
            <section class="qa-block">
              <h2>Tra loi</h2>
              <div>${answerHtml || ''}</div>
            </section>
          </body>
        </html>
      `)
      printWindow.document.close()
      return printWindow
    },
    printAnswer (question, answer) {
      const printWindow = this.createPrintWindow('SuperDoc Answer', question, this.compiledMarkdown(answer))
      if (!printWindow) return
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 200)
    },
    exportAnswerPdf (question, answer) {
      const printWindow = this.createPrintWindow('SuperDoc PDF', question, this.compiledMarkdown(answer))
      if (!printWindow) return
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 200)
    },
    bindMessageScroll () {
      const container = this.$refs.messageContainer
      if (!container) return
      container.removeEventListener('scroll', this.handleMessageScroll)
      container.addEventListener('scroll', this.handleMessageScroll, { passive: true })
      this.handleMessageScroll()
    },
    unbindMessageScroll () {
      const container = this.$refs.messageContainer
      if (container) container.removeEventListener('scroll', this.handleMessageScroll)
    },
    handleMessageScroll () {
      const container = this.$refs.messageContainer
      if (!container) return
      const distanceFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop
      this.showScrollToBottom = distanceFromBottom > this.scrollThreshold
    },
    scrollToLatestQuestion () {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer
        const question = Array.isArray(this.$refs.latestQuestion)
          ? this.$refs.latestQuestion[0]
          : this.$refs.latestQuestion
        const questionElement = question?.$el || question
        if (container && questionElement) {
          container.scrollTop = Math.max(0, questionElement.offsetTop - container.offsetTop - 12)
          this.handleMessageScroll()
        }
      })
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer
        if (container) {
          container.scrollTop = container.scrollHeight
          this.handleMessageScroll()
        }
      })
    },
    handleClose () {
      this.unbindMessageScroll()
      this.showScrollToBottom = false
      if (this.askAbortController) {
        this.askAbortController.abort()
        this.askAbortController = null
      }
      this.conversations.forEach((conversation) => {
        ;(conversation.messages || []).forEach((message) => this.releaseMessageUploads(message))
      })
      this.question = ''
    }
  }
}
</script>

<style scoped>
:deep(.superdoc-popup-dialog) {
  width: 80% !important;
  max-width: 80% !important;
  margin-top: 5vh !important;
}

:deep(.superdoc-popup-dialog .el-dialog__header) {
  display: none;
}

:deep(.superdoc-popup-dialog .el-dialog__body) {
  padding: 0;
}

.superdoc-popup {
  width: 100%;
  height: 90vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.superdoc-popup-header {
  position: sticky;
  top: 0;
  z-index: 20;
  min-height: 56px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: #fff;
}

.superdoc-popup-logo {
  width: 24px;
  height: 24px;
}

.superdoc-popup-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.superdoc-popup-close {
  border: 0;
  background: transparent;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  color: #64748b;
}

.superdoc-popup-close:hover {
  background: #f1f5f9;
}

.superdoc-popup-content {
  flex: 1;
  display: flex;
  min-height: 0;
  height: calc(90vh - 56px);
}

.superdoc-popup-sidebar {
  width: 84px;
  border-right: 1px solid #e2e8f0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  transition: width 0.2s ease;
}

.superdoc-popup-sidebar--expanded {
  width: 260px;
}

.history-panel-head {
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-panel-head--expanded {
  justify-content: space-between;
}

.history-panel-title {
  font-size: 13px;
  color: #475569;
  font-weight: 700;
}

.history-panel-toggle {
  width: 28px;
  height: 28px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  color: #2563eb;
  background: #eff6ff;
}

.new-chat-btn {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.new-chat-btn--collapsed {
  padding: 10px;
}

.history-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
}

.history-item {
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 4px 15px;
  transition: all 0.2s ease;
}

.history-item.active {
  background: #eff6ff;
  border-color: #bfdbfe;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.history-item--collapsed {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
  display: flex;
  justify-content: center;
}

.history-item-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.history-avatar-btn {
  width: 38px;
  height: 38px;
  border-radius: 9999px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
}

.history-avatar-btn.active {
  border-color: #2563eb;
  background: #dbeafe;
}

.history-main-btn {
  flex: 1;
  min-width: 0;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.history-main-btn:focus,
.history-main-btn:focus-visible {
  outline: none;
}

.history-title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
}

.history-icon-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.history-icon-btn:hover {
  color: #3b82f6;
  background: rgba(255, 255, 255, 0.8);
}

.history-icon-btn:focus,
.history-icon-btn:focus-visible {
  outline: none;
}

:deep(.history-actions-popover) {
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
}

:deep(.history-action-menu) {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.history-action-menu button {
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 8px 10px;
  text-align: left;
  width: 100%;
  transition: background 0.15s ease, color 0.15s ease;
}

.history-action-menu button:hover {
  background: #eff6ff;
  color: #2563eb;
}

.history-action-menu i {
  color: #64748b;
  margin-right: 6px;
  width: 14px;
  text-align: center;
}

:deep(.history-action-menu .history-action-menu__danger:hover) {
  background: #fef2f2;
  color: #ef4444;
}

:deep(.history-action-menu .history-action-menu__danger:hover i) {
  color: #ef4444;
}

.superdoc-popup-chat {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.superdoc-popup-messages {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

.superdoc-popup-input {
  border-top: 1px solid #e2e8f0;
  padding: 12px;
}

.chat-scroll-actions {
  position: absolute;
  left: 50%;
  bottom: 195px;
  z-index: 5;
  transform: translateX(-50%);
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.chat-scroll-actions:hover {
  opacity: 1;
}

.chat-scroll-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #fff;
  color: #2563eb;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

.chat-scroll-btn:hover {
  background: #eff6ff;
}

.welcome-centered {
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* min-height: 100%; */
}

.dot-loading-wrap {
  display: flex;
  justify-content: flex-start;
  padding: 4px 0 8px;
}

.dot-loading {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.dot-loading-item {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #64748b;
  animation: dotPulse 1.1s infinite ease-in-out;
}

.dot-loading-item:nth-child(2) {
  animation-delay: 0.15s;
}

.dot-loading-item:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes dotPulse {
  0%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
</style>
